import React, { useEffect } from 'react';

import { Box, ToggleButton } from '@zillow/constellation-10';

import type { SwitchObject } from '../../../models/Types';
import { SMOKING_POLICY } from '../../../constants';
import type { Section } from '../../../models';
import { SmokingPolicySection } from '../../../models';
import SwitchesWithTitle from '../../Widgets/SwitchesWithTitle';
import SmokingAdditionalAreas from './Smoking/SmokingAdditionalAreas';
import SmokingButtonGroup from './Smoking/SmokingButtonGroup';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: SmokingPolicySection) => void;
};

export default function SmokingPolicy_v3({ section, setSection, setSectionValues }: Props): JSX.Element {
  useEffect(() => {
    const smokingPolicySection = new SmokingPolicySection(section.initialParams);
    setSection(smokingPolicySection);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  function onRemoveAll(): void {
    section.removeAllIndexes();
    setSection(section);
  }

  function onNoSmokingClick(): void {
    setSectionValues({
      [SMOKING_POLICY.FIELD_NAME.NO_SMOKING]: true,
      [SMOKING_POLICY.FIELD_NAME.CERTAIN_AREAS]: false,
      [SMOKING_POLICY.FIELD_NAME.ALLOWED_THROUGHOUT_PROPERTY]: false,
    });
    onRemoveAll();
  }

  function onCertainAreasClick(): void {
    setSectionValues({
      [SMOKING_POLICY.FIELD_NAME.NO_SMOKING]: false,
      [SMOKING_POLICY.FIELD_NAME.CERTAIN_AREAS]: true,
      [SMOKING_POLICY.FIELD_NAME.ALLOWED_THROUGHOUT_PROPERTY]: false,
    });
  }

  function onInsideUnitToggle(): void {
    toggleSwitch(SMOKING_POLICY.FIELD_NAME.INSIDE_UNIT);
  }

  function onOutsideMoreThan10FeetToggle(): void {
    toggleSwitch(SMOKING_POLICY.FIELD_NAME.OUTSIDE_MORE_THAN_10_FEET);
  }

  function onPorchesPatiosYardsToggle(): void {
    toggleSwitch(SMOKING_POLICY.FIELD_NAME.PORCHES_PATIOS_YARDS);
  }

  function toggleSwitch(key: string): void {
    setSectionValues({
      [key]: !section.getFieldValue(key),
    });
  }

  const isNoSmoking = section.getFieldValue(SMOKING_POLICY.FIELD_NAME.NO_SMOKING);
  const isCertainAreas = section.getFieldValue(SMOKING_POLICY.FIELD_NAME.CERTAIN_AREAS);
  const smokingPolicies: Array<SwitchObject> = [
    {
      text: 'Inside the unit',
      onClick: onInsideUnitToggle,
      toggled: section.getFieldValue(SMOKING_POLICY.FIELD_NAME.INSIDE_UNIT),
    },
    {
      text: 'Outside, more than 10 feet from building',
      onClick: onOutsideMoreThan10FeetToggle,
      toggled: section.getFieldValue(SMOKING_POLICY.FIELD_NAME.OUTSIDE_MORE_THAN_10_FEET),
    },
    {
      text: 'Porches, patios, and yards adjacent to units',
      onClick: onPorchesPatiosYardsToggle,
      toggled: section.getFieldValue(SMOKING_POLICY.FIELD_NAME.PORCHES_PATIOS_YARDS),
    },
  ];

  return (
    <>
      <SmokingButtonGroup>
        <ToggleButton data-test="no-smoking" onClick={onNoSmokingClick} selected={isNoSmoking}>
          No, smoking not allowed
        </ToggleButton>
        <ToggleButton data-test="certain-areas" onClick={onCertainAreasClick} selected={isCertainAreas}>
          Yes, allowed in certain areas
        </ToggleButton>
      </SmokingButtonGroup>
      {isCertainAreas && (
        <Box css={{ marginTop: 'layout.tight' }}>
          <SwitchesWithTitle switches={smokingPolicies} legend="Where is smoking allowed?" noBottomMargin />
          <SmokingAdditionalAreas section={section} setSection={setSection} setSectionValues={setSectionValues} />
        </Box>
      )}
    </>
  );
}

SmokingPolicy_v3.description = 'Removal of common area option and smoking throughout property.';
